import type { EnrichedEventOut } from '../analyticsTypes';

export interface FunnelStepDefinition {
  id: string;
  label: string;
  /** Match if the event name equals one of these. */
  events: string[];
  /** Optional payload predicate. Receives the `meta` object. */
  match?: (meta: Record<string, unknown>) => boolean;
}

export interface FunnelStepResult {
  id: string;
  label: string;
  count: number;
  /** Conversion rate from the previous step (0..1). 1.0 for the first step. */
  rateFromPrevious: number;
  /** Conversion rate from the first step (0..1). */
  rateFromStart: number;
  /** Number of sessions that dropped off between previous step and this step. */
  dropOff: number;
}

export interface ComputeFunnelOptions {
  /** Group by session_id (default), actor_id, or treat each event independently. */
  groupBy?: 'session_id' | 'actor_id' | 'none';
}

/**
 * Compute a sequential funnel from a list of enriched events.
 * Each step is satisfied by a session/actor when it has at least one event
 * matching `events` AND (if provided) `match(meta)` AFTER the previous step.
 *
 * Pure frontend rollup — events should already be filtered to the date range.
 */
export function computeFunnel(
  events: EnrichedEventOut[],
  steps: FunnelStepDefinition[],
  options: ComputeFunnelOptions = {},
): FunnelStepResult[] {
  if (steps.length === 0) return [];
  const groupBy = options.groupBy ?? 'session_id';

  if (groupBy === 'none') {
    return steps.map((step, idx) => {
      const count = events.filter(matchesStep(step)).length;
      const prev = idx === 0 ? count : steps.slice(0, idx).reduce((acc, s) => Math.min(acc, events.filter(matchesStep(s)).length), Infinity);
      const first = events.filter(matchesStep(steps[0])).length;
      return {
        id: step.id,
        label: step.label,
        count,
        rateFromPrevious: prev === 0 ? 0 : count / prev,
        rateFromStart: first === 0 ? 0 : count / first,
        dropOff: Math.max(0, prev - count),
      };
    });
  }

  // Sort once, ascending by timestamp.
  const sorted = [...events].sort(
    (a, b) => new Date(a.event_created_at).getTime() - new Date(b.event_created_at).getTime(),
  );
  const groups = new Map<string, EnrichedEventOut[]>();
  for (const ev of sorted) {
    const key = (groupBy === 'session_id' ? ev.session_id : null) ?? '';
    if (!key) continue;
    const arr = groups.get(key) ?? [];
    arr.push(ev);
    groups.set(key, arr);
  }

  const stepCounts = steps.map(() => 0);

  groups.forEach((groupEvents) => {
    let cursor = 0;
    let stepIdx = 0;
    while (stepIdx < steps.length && cursor < groupEvents.length) {
      const step = steps[stepIdx];
      const found = groupEvents.findIndex((ev, idx) => idx >= cursor && matchesStep(step)(ev));
      if (found === -1) return;
      stepCounts[stepIdx] += 1;
      cursor = found + 1;
      stepIdx += 1;
    }
  });

  const first = stepCounts[0] || 0;
  return steps.map((step, idx) => {
    const count = stepCounts[idx];
    const prev = idx === 0 ? count : stepCounts[idx - 1];
    return {
      id: step.id,
      label: step.label,
      count,
      rateFromPrevious: prev === 0 ? 0 : count / prev,
      rateFromStart: first === 0 ? 0 : count / first,
      dropOff: Math.max(0, prev - count),
    };
  });
}

function matchesStep(step: FunnelStepDefinition) {
  return (event: EnrichedEventOut): boolean => {
    if (!step.events.includes(event.received_event_name)) return false;
    if (step.match) return step.match(event.meta ?? {});
    return true;
  };
}

/** Pre-defined funnels for the three flows the dashboard surfaces by default. */
export const WIZARD_FUNNEL: FunnelStepDefinition[] = [
  { id: 'opened', label: 'Opened', events: ['wizard_opened'] },
  { id: 'step_viewed', label: 'Step viewed', events: ['wizard_step_viewed'] },
  { id: 'step_completed', label: 'Step completed', events: ['wizard_step_completed'] },
  { id: 'completed', label: 'Completed', events: ['wizard_completed'] },
];

export const POSTCODE_FUNNEL: FunnelStepDefinition[] = [
  { id: 'file_added', label: 'File added', events: ['postcode_assignment_file_added'] },
  { id: 'started', label: 'Run started', events: ['postcode_assignment_started'] },
  { id: 'completed', label: 'Run completed', events: ['postcode_assignment_completed'] },
];

export const FLEET_OPTIMISER_FUNNEL: FunnelStepDefinition[] = [
  { id: 'configured', label: 'Configured', events: ['fleet_optimiser_field_group_changed'] },
  { id: 'analyzed', label: 'Analyze clicked', events: ['fleet_optimiser_analyze_clicked'] },
  { id: 'result', label: 'Result displayed', events: ['fleet_optimiser_result_displayed'] },
];

const SIMULATION_RESULTS_EVENTS = [
  'simulation_results_tab_changed',
  'simulation_results_reloaded',
  'simulation_results_path_mode_changed',
  'simulation_results_vehicle_row_selected',
  'simulation_results_vehicle_visibility_toggled',
  'simulation_results_unallocated_visibility_changed',
  'simulation_results_timeline_window_changed',
  'simulation_results_stop_clicked',
  'simulation_results_modal_opened',
];

export const SIMULATION_FUNNEL: FunnelStepDefinition[] = [
  { id: 'started', label: 'Run started', events: ['simulation_run_start'] },
  { id: 'completed', label: 'Run completed', events: ['simulation_run_complete'] },
  { id: 'viewed', label: 'Results viewed', events: SIMULATION_RESULTS_EVENTS },
];
